export interface ErrorMessages {
  [key: string]: string;
}

export interface FieldErrorMessages {
  [fieldName: string]: ErrorMessages;
}

export class FormErrorMessages {
  // Common error messages
  static readonly COMMON_ERRORS: ErrorMessages = {
    'required': 'This field is required',
    'minlength': 'Must be at least {minLength} characters long',
    'maxlength': 'Cannot exceed {maxLength} characters',
    'min': 'Must be at least {min}',
    'max': 'Cannot exceed {max}',
    'pattern': 'Has invalid format',
    'email': 'Must be a valid email address',
    'invalidFormat': 'Please enter a valid format',
    'invalidRange': 'Value must be between {min} and {max}',
    'invalidPositiveNumber': 'Please enter a valid positive number',
    'invalidPositiveNumberFormat': 'Please enter a valid number (e.g., 100 or 100.5)',
    'futureDate': 'Date cannot be in the future',
    'pastDate': 'Date cannot be in the past',
    'invalidDateRange': 'End date must be after start date',
    'invalidFileType': 'Invalid file type. Please upload {allowedTypes}',
    'fileTooLarge': 'File size must be less than {maxSize}MB',
    'invalidFileSize': 'File size is invalid',
    'consecutiveSpaces': 'Cannot have consecutive spaces',
    'invalidCharacters': 'Contains invalid characters',
    'invalidLength': 'Must be at least {minLength} characters long',
    'invalidValue': 'Please enter a valid value',
    'invalidMobileMinLength': 'Phone number must be at least {minLength} digits',
    'invalidMobileMaxLength': 'Phone number cannot exceed {maxLength} digits'
  };

  // Vehicle-specific error messages
  static readonly VEHICLE_ERRORS: FieldErrorMessages = {
    'registrationNumber': {
      'required': 'Registration number is required',
      'invalidRegistrationNumber': 'Please enter a valid Indian vehicle registration number (e.g., KA01AB1234)',
      'invalidFormat': 'Please enter a valid registration number format'
    },
    'chasisNumber': {
      'required': 'Chassis number is required',
      'invalidchasisNumber': 'Please enter a valid 17-character VIN number(e.g., 1G1RB67XCA1234567)',
      'invalidFormat': 'Chassis number must be exactly 17 characters'
    },
    'yearOfManufacture': {
      'required': 'Year of manufacture is required',
      'invalidYearFormat': 'Please enter a valid 4-digit year (e.g., 2020)',
      'invalidYear': 'Year must be between 1900 and current year + 1',
      'invalidFormat': 'Please enter a valid year format'
    },
    'vehicleMake': {
      'required': 'Vehicle make is required',
      'minlength': 'Vehicle make must be at least 2 characters long',
      'invalidFormat': 'Please enter a valid vehicle make'
    },
    'vehicleModel': {
      'required': 'Vehicle model is required',
      'minlength': 'Vehicle model must be at least 2 characters long',
      'invalidFormat': 'Please enter a valid vehicle model'
    },
    'vehicleType': {
      'required': 'Vehicle type is required',
      'invalidVehicleType': 'Please select a valid vehicle type (Truck, Trailer, Bus, Car, Van, etc.)',
      'invalidFormat': 'Please select a valid vehicle type'
    },
    'loadCapacity': {
      'required': 'Load capacity is required',
      'invalidLoadCapacityFormat': 'Please enter a valid number (e.g., 1000 or 1000.5)',
      'invalidLoadCapacity': 'Please enter a valid load capacity',
      'invalidLoadCapacityRange': 'Load capacity must be between 100kg and 100,000kg',
      'invalidFormat': 'Please enter a valid load capacity'
    },
    'volLoadCapacity': {
      'invalidVolumetricLoadCapacityFormat': 'Please enter a valid number (e.g., 500 or 500.25)',
      'invalidVolumetricLoadCapacity': 'Please enter a valid volumetric load capacity',
      'invalidVolumetricLoadCapacityRange': 'Volumetric load capacity cannot exceed 50,000kg',
      'invalidFormat': 'Please enter a valid volumetric load capacity'
    },
    'fuelType': {
      'required': 'Fuel type is required',
      'invalidFuelType': 'Please select a valid fuel type (Petrol, Diesel, CNG, LPG, Electric, Hybrid, etc.)',
      'invalidFormat': 'Please select a valid fuel type'
    },
    'speed': {
      'required': 'Speed is required',
      'invalidSpeedFormat': 'Please enter a valid number (e.g., 80 or 80.5)',
      'invalidSpeed': 'Speed must be between 0 and 200 km/h',
      'invalidFormat': 'Please enter a valid speed'
    },
    'mileage': {
      'required': 'Mileage is required',
      'invalidMileageFormat': 'Please enter a valid number (e.g., 1 or 50)',
      'invalidMileage': 'Mileage must be between 0 and 50 km',
      'invalidFormat': 'Please enter a valid mileage'
    },
    'numberOfAxles': {
      'required': 'Number of axles is required',
      'invalidNumberOfAxlesFormat': 'Please enter a valid number between 1 and 10',
      'invalidNumberOfAxles': 'Vehicle must have between 1 and 10 axles',
      'invalidFormat': 'Please enter a valid number of axles'
    },
    'vehicleColor': {
      'required': 'Vehicle color is required',
      'invalidColorLength': 'Color must be at least 2 characters long',
      'invalidColorChars': 'Color can only contain letters, spaces, and hyphens',
      'invalidColorSpaces': 'Color cannot have consecutive spaces',
      'invalidColorFormat': 'Color must start and end with a letter',
      'invalidFormat': 'Please enter a valid vehicle color'
    },
    'wheelbase': {
      'invalidWheelbase': 'Please enter a valid wheelbase (e.g., "3.5m", "12ft", "3500mm")',
      'invalidWheelbaseRange': 'Wheelbase must be between 0 and 100 units',
      'invalidFormat': 'Please enter a valid wheelbase format'
    },
    'vehicleFeet': {
      'required': 'Vehicle feet is required',
      'invalidVehicleFeet': 'Please enter a valid vehicle feet (e.g., "20ft", "40")',
      'invalidVehicleFeetRange': 'Vehicle feet must be between 1 and 100',
      'invalidFormat': 'Please enter a valid vehicle feet'
    },
    'serviceInterval': {
      'invalidServiceIntervalFormat': 'Please enter a valid number (e.g., 5000 or 5000.5)',
      'invalidServiceInterval': 'Service interval must be between 0 and 100,000 km',
      'invalidFormat': 'Please enter a valid service interval'
    },
    'currentOdometerReading': {
      'invalidOdometerReadingFormat': 'Please enter a valid number (e.g., 100000 or 100000.5)',
      'invalidOdometerReading': 'Odometer reading must be between 0 and 9,999,999 km',
      'invalidFormat': 'Please enter a valid odometer reading'
    },
    'gpsVendorName': {
      'required': 'GPS provider is required when GPS is enabled',
      'invalidgpsVendorNameLength': 'GPS provider must be at least 2 characters long',
      'invalidgpsVendorNameChars': 'GPS provider can only contain letters, numbers, spaces, dots, and hyphens',
      'invalidgpsVendorNameSpaces': 'GPS provider cannot have consecutive spaces',
      'invalidFormat': 'Please enter a valid GPS provider'
    },
    'lastServiceDate': {
      'required': 'Last service date is required',
      'futureServiceDate': 'Service date cannot be in the future',
      'invalidFormat': 'Please select a valid service date'
    },
    'nextServiceDue': {
      'required': 'Next service due date is required',
      'invalidServiceDateRange': 'Next service date must be after last service date',
      'invalidFormat': 'Please select a valid service date'
    }
  };

  // Dimension-specific error messages
  static readonly DIMENSION_ERRORS: FieldErrorMessages = {
    'length': {
      'required': 'Length is required',
      'invalidDimensionFormat': 'Please enter a valid number (e.g., 20 or 20.5)',
      'invalidDimension': 'Please enter a valid length',
      'invalidDimensionRange': 'Length must be between 1 and 100 feet',
      'invalidFormat': 'Please enter a valid length'
    },
    'breadth': {
      'required': 'Breadth is required',
      'invalidDimensionFormat': 'Please enter a valid number (e.g., 8 or 8.25)',
      'invalidDimension': 'Please enter a valid breadth',
      'invalidDimensionRange': 'Breadth must be between 0.5 and 20 feet',
      'invalidFormat': 'Please enter a valid breadth'
    },
    'height': {
      'required': 'Height is required',
      'invalidDimensionFormat': 'Please enter a valid number (e.g., 10 or 10.5)',
      'invalidDimension': 'Please enter a valid height',
      'invalidDimensionRange': 'Height must be between 0.5 and 15 feet',
      'invalidFormat': 'Please enter a valid height'
    }
  };

  // Dimension ratio error messages
  static readonly DIMENSION_RATIO_ERRORS: ErrorMessages = {
    'invalidLengthBreadthRatio': 'Length should be significantly greater than breadth for most vehicles',
    'invalidLengthHeightRatio': 'Length should be significantly greater than height',
    'invalidBreadthHeightRatio': 'Breadth to height ratio seems unusual. Please verify dimensions.'
  };

  // Driver-specific error messages
  static readonly DRIVER_ERRORS: FieldErrorMessages = {
    'driverName': {
      'required': 'Driver name is required',
      'minlength': 'Driver name must be at least 2 characters long',
      'invalidFormat': 'Please enter a valid driver name'
    },
    'firstName': {
      'required': 'First name is required',
      'minlength': 'First name must be at least 2 characters long',
      'invalidFormat': 'Please enter a valid first name'
    },
    'lastName': {
      'required': 'Last name is required',
      'minlength': 'Last name must be at least 2 characters long',
      'invalidFormat': 'Please enter a valid last name'
    },
    'email': {
      'required': 'Email is required',
      'email': 'Please enter a valid email address',
      'invalidFormat': 'Please enter a valid email format'
    },
    'phone': {
      'required': 'Phone number is required',
      'pattern': 'Please enter a valid 10-digit phone number',
      'invalidFormat': 'Please enter a valid phone number'
    },
    'phoneNumber': {
      'required': 'Phone number is required',
      'pattern': 'Please enter a valid 10-digit phone number',
      'invalidFormat': 'Please enter a valid phone number'
    },
    'dob': {
      'required': 'Date of birth is required',
      'futureDate': 'Date of birth cannot be in the future',
      'invalidAge': 'Driver must be at least 18 years old',
      'invalidFormat': 'Please select a valid date of birth'
    },
    'dateOfBirth': {
      'required': 'Date of birth is required',
      'futureDate': 'Date of birth cannot be in the future',
      'invalidAge': 'Driver must be at least 18 years old',
      'invalidFormat': 'Please select a valid date of birth'
    },
    'driverType': {
      'required': 'Driver type is required',
      'invalidFormat': 'Please select a valid driver type'
    },
    'lastOrganizationName': {
      'required': 'Last organization name is required',
      'minlength': 'Last organization name must be at least 2 characters long',
      'invalidFormat': 'Please enter a valid organization name'
    },
    'addressLine1': {
      'required': 'Address line 1 is required',
      'minlength': 'Address line 1 must be at least 5 characters long',
      'invalidFormat': 'Please enter a valid address'
    },
    'addressLine2': {
      'invalidFormat': 'Please enter a valid address line 2'
    },
    'pincode': {
      'required': 'Pincode is required',
      'invalidPincodeChars': 'Pincode can only contain digits',
      'invalidPincodeLength': 'Pincode must be exactly 6 digits',
      'invalidPincodeZeros': 'Pincode cannot be all zeros',
      'invalidPincodeRepeated': 'Pincode cannot have all same digits',
      'invalidPincodeSequential': 'Pincode cannot be sequential numbers',
      'invalidPincodeRepeatedGroups': 'Pincode has invalid repeated patterns',
      'invalidPincodeFirstDigit': 'Pincode first digit cannot be 0',
      'invalidPincodeZone': 'Pincode first digit must be between 1 and 9',
      'invalidPincodePattern': 'Pincode pattern is invalid',
      'invalidFormat': 'Please enter a valid 6-digit pincode'
    },
    'city': {
      'required': 'City is required',
      'minlength': 'City must be at least 2 characters long',
      'invalidFormat': 'Please enter a valid city name'
    },
    'state': {
      'required': 'State is required',
      'minlength': 'State must be at least 2 characters long',
      'invalidFormat': 'Please enter a valid state name'
    },
    'country': {
      'required': 'Country is required',
      'invalidFormat': 'Please enter a valid country name'
    },
    'aadhar': {
      'required': 'Aadhar number is required',
      'invalidAadhar': 'Please enter a valid 12-digit Aadhar number (should not start with 0 or 1)',
      'invalidFormat': 'Please enter a valid Aadhar number'
    },
    'aadharNumber': {
      'required': 'Aadhar number is required',
      'invalidAadhar': 'Please enter a valid 12-digit Aadhar number (should not start with 0 or 1)',
      'invalidFormat': 'Please enter a valid Aadhar number'
    },
    'pan': {
      'required': 'PAN number is required',
      'invalidPan': 'Please enter a valid PAN number format (e.g., ABCDE1234F)',
      'invalidPanEntity': 'PAN entity type is invalid',
      'invalidFormat': 'Please enter a valid PAN number'
    },
    'panNumber': {
      'required': 'PAN number is required',
      'invalidPan': 'Please enter a valid PAN number format (e.g., ABCDE1234F)',
      'invalidPanEntity': 'PAN entity type is invalid',
      'invalidFormat': 'Please enter a valid PAN number'
    },
    'gstNumber': {
      'required': 'GST number is required',
      'invalidGstLength': 'GST number must be exactly 15 characters long',
      'invalidGstStateCode': 'GST number contains invalid state code',
      'invalidGstPan': 'GST number contains invalid PAN format',
      'invalidGstRegistrationNumber': 'GST registration number must be between 1-9',
      'invalidGstFormat': 'GST number format is invalid (14th character must be Z)',
      'invalidGstCheckDigit': 'GST number has invalid check digit',
      'invalidFormat': 'Please enter a valid GST number (e.g., 07AAAAA0000A1Z5)'
    },
    'gstYesNo': {
      'required': 'Please select whether you have GST number',
      'invalidFormat': 'Please select a valid option'
    },
    'vendorId': {
      'required': 'Vendor is required',
      'invalidFormat': 'Please select a valid vendor'
    },
    'emergencyName': {
      'required': 'Emergency contact name is required',
      'minlength': 'Emergency contact name must be at least 2 characters long',
      'invalidFormat': 'Please enter a valid contact name'
    },

    'emergencyRelation': {
      'required': 'Emergency relation is required',
      'invalidFormat': 'Please select a valid relation'
    },
    'licenseNumber': {
      'required': 'License number is required',
      'invalidDrivingLicense': 'Please enter a valid DL number (2 letters followed by numbers, e.g., KA0120110149649)',
      'invalidDrivingLicenseLength': 'DL number must be exactly 15 characters (e.g., KA0120110149649)',
      'pattern': 'Please enter a valid license number format',
      'invalidFormat': 'Please enter a valid license number'
    },
    'licenseValidity': {
      'required': 'License validity is required',
      'invalidFormat': 'Please select a valid license validity date'
    },
    'language': {
      'invalidFormat': 'Please select a valid language'
    },
    'experience': {
      'required': 'Driving experience is required',
      'invalidFormat': 'Please enter a valid experience'
    },
    'dlClass': {
      'required': 'DL class is required',
      'invalidFormat': 'Please select a valid DL class'
    },
    'accidentHistory': {
      'required': 'Accident history is required',
      'invalidFormat': 'Please select a valid option'
    },
    'address': {
      'required': 'Address is required',
      'minlength': 'Address must be at least 10 characters long',
      'invalidFormat': 'Please enter a valid address'
    }
  };

  // Vendor-specific error messages
  static readonly VENDOR_ERRORS: FieldErrorMessages = {
    'vendorName': {
      'required': 'Vendor name is required',
      'minlength': 'Vendor name must be at least 3 characters long',
      'invalidFormat': 'Please enter a valid vendor name'
    },
    'organizationName': {
      'required': 'Organization name is required',
      'minlength': 'Organization name must be at least 3 characters long',
      'invalidFormat': 'Please enter a valid organization name'
    },
    'ownerName': {
      'required': 'Owner name is required',
      'minlength': 'Owner name must be at least 3 characters long',
      'invalidFormat': 'Please enter a valid owner name'
    },
    'email': {
      'required': 'Email is required',
      'invalidEmailFormat': 'Please enter a valid email address format(example@example.com)',
      'invalidEmailDisposable': 'Disposable email addresses are not allowed',
      'invalidEmailConsecutiveDots': 'Email cannot have consecutive dots',
      'invalidEmailChars': 'Email contains invalid characters',
      'invalidEmailLocalLength': 'Email local part must be between 1 and 64 characters',
      'invalidEmailDomainLength': 'Email domain must be between 3 and 253 characters',
      'invalidEmailTld': 'Email must have a valid top-level domain (2-6 characters)',
      'invalidFormat': 'Please enter a valid email address(example@gmail.com)'
    },
    'phone': {
      'required': 'Phone number is required',
      'invalidMobileChars': 'Phone number can only contain digits',
      'invalidMobileMinLength': 'Phone number must be at least {minLength} digits',
      'invalidMobileMaxLength': 'Phone number cannot exceed {maxLength} digits',
      'invalidMobileLength': 'Phone number must be exactly 10 digits',
      'invalidMobilePrefix': 'Phone number must start with 6, 7, 8, or 9',
      'invalidMobileRepeated': 'Phone number cannot have all same digits',
      'invalidMobilePattern': 'Phone number pattern is invalid',
      'invalidMobileSequential': 'Phone number cannot be sequential',
      'invalidMobileRepeatedGroups': 'Phone number has invalid repeated patterns',
      'invalidFormat': 'Please enter a valid 10-digit phone number'
    },
    'emergencyPhone': {
      'required': 'Emergency phone number is required',
      'invalidMobileChars': 'Phone number can only contain digits',
      'invalidMobileMinLength': 'Phone number must be at least {minLength} digits',
      'invalidMobileMaxLength': 'Phone number cannot exceed {maxLength} digits',
      'invalidMobileLength': 'Phone number must be exactly 10 digits',
      'invalidMobilePrefix': 'Phone number must start with 6, 7, 8, or 9',
      'invalidMobileRepeated': 'Phone number cannot have all same digits',
      'invalidMobilePattern': 'Phone number pattern is invalid',
      'invalidMobileSequential': 'Phone number cannot be sequential',
      'invalidMobileRepeatedGroups': 'Phone number has invalid repeated patterns',
      'invalidFormat': 'Please enter a valid 10-digit phone number'
    },
    'vendorType': {
      'required': 'Vendor type is required',
      'invalidFormat': 'Please select a valid vendor type'
    },
    'billingCycle': {
      'required': 'Billing cycle is required',
      'invalidFormat': 'Please select a valid billing cycle'
    },
    'addressLine1': {
      'required': 'Address line 1 is required',
      'minlength': 'Address line 1 must be at least 5 characters long',
      'invalidFormat': 'Please enter a valid address'
    },
    'addressLine2': {
      'invalidFormat': 'Please enter a valid address line 2'
    },
    'pincode': {
      'required': 'Pincode is required',
      'invalidPincodeChars': 'Pincode can only contain digits',
      'invalidPincodeLength': 'Pincode must be exactly 6 digits',
      'invalidPincodeZeros': 'Pincode cannot be all zeros',
      'invalidPincodeRepeated': 'Pincode cannot have all same digits',
      'invalidPincodeSequential': 'Pincode cannot be sequential numbers',
      'invalidPincodeRepeatedGroups': 'Pincode has invalid repeated patterns',
      'invalidPincodeFirstDigit': 'Pincode first digit cannot be 0',
      'invalidPincodeZone': 'Pincode first digit must be between 1 and 9',
      'invalidPincodePattern': 'Pincode pattern is invalid',
      'invalidFormat': 'Please enter a valid 6-digit pincode'
    },
    'city': {
      'required': 'City is required',
      'minlength': 'City must be at least 2 characters long',
      'invalidFormat': 'Please enter a valid city name'
    },
    'state': {
      'required': 'State is required',
      'minlength': 'State must be at least 2 characters long',
      'invalidFormat': 'Please enter a valid state name'
    },
    'country': {
      'required': 'Country is required',
      'invalidFormat': 'Please enter a valid country name'
    },
    'aadhar': {
      'required': 'Aadhar number is required',
      'invalidAadhar': 'Please enter a valid 12-digit Aadhar number (should not start with 0 or 1)',
      'invalidFormat': 'Please enter a valid Aadhar number format'
    },
    'pan': {
      'required': 'PAN number is required',
      'invalidPan': 'Please enter a valid PAN number format (e.g., ABCDE1234F)',
      'invalidPanEntity': 'PAN entity type is invalid',
      'invalidFormat': 'Please enter a valid PAN number'
    },
    'gstNumber': {
      'required': 'GST number is required',
      'invalidGstLength': 'GST number must be exactly 15 characters long',
      'invalidGstStateCode': 'GST number contains invalid state code',
      'invalidGstPan': 'GST number contains invalid PAN format',
      'invalidGstRegistrationNumber': 'GST registration number must be between 1-9',
      'invalidGstFormat': 'GST number format is invalid (14th character must be Z)',
      'invalidGstCheckDigit': 'GST number has invalid check digit',
      'invalidFormat': 'Please enter a valid GST number (e.g., 07AAAAA0000A1Z5)'
    },
    'gstYesNo': {
      'required': 'Please select whether you have GST number',
      'invalidFormat': 'Please select a valid option'
    },
    // Bank-related error messages
    'accountHolder': {
      'required': 'Account holder name is required',
      'invalidAccountHolderLength': 'Account holder name must be at least 3 characters long',
      'invalidAccountHolderChars': 'Account holder name can only contain letters, spaces, dots, and hyphens',
      'invalidAccountHolderSpaces': 'Account holder name cannot have consecutive spaces',
      'invalidAccountHolderFormat': 'Account holder name must start and end with a letter',
      'invalidFormat': 'Please enter a valid account holder name'
    },
    'bankName': {
      'required': 'Bank name is required',
      'invalidBankNameLength': 'Bank name must be at least 3 characters long',
      'invalidBankNameChars': 'Bank name can only contain letters, numbers, spaces, dots, hyphens, and ampersands',
      'invalidBankNameSpaces': 'Bank name cannot have consecutive spaces',
      'invalidBankNameFormat': 'Bank name must start with a letter and end with alphanumeric',
      'invalidFormat': 'Please enter a valid bank name'
    },
    'accountNumber': {
      'required': 'Account number is required',
      'invalidAccountNumberChars': 'Account number can only contain digits',
      'invalidAccountNumberLength': 'Account number must be between 9 and 18 digits',
      'invalidAccountNumberZeros': 'Account number cannot be all zeros',
      'invalidAccountNumberRepeated': 'Account number cannot have all same digits',
      'invalidFormat': 'Please enter a valid account number'
    },
    'confirmAccountNumber': {
      'required': 'Please confirm your account number',
      'accountNumberMismatch': 'Account numbers do not match',
      'invalidFormat': 'Please enter a valid account number'
    },
    'ifsc': {
      'required': 'IFSC code is required',
      'invalidIfsc': 'Please enter a valid IFSC code format (e.g., SBIN0001234)',
      'invalidIfscBankCode': 'IFSC bank code is not recognized',
      'invalidFormat': 'Please enter a valid IFSC code'
    },
    'branch': {
      'required': 'Branch name is required',
      'invalidBranchLength': 'Branch name must be at least 3 characters long',
      'invalidBranchChars': 'Branch name can only contain letters, numbers, spaces, dots, hyphens, and ampersands',
      'invalidBranchSpaces': 'Branch name cannot have consecutive spaces',
      'invalidBranchFormat': 'Branch name must start with a letter and end with alphanumeric',
      'invalidFormat': 'Please enter a valid branch name'
    },
    'bankPhone': {
      'required': 'Bank phone number is required',
      'invalidBankPhoneChars': 'Phone number can only contain digits',
      'invalidBankPhoneLength': 'Phone number must be exactly 10 digits',
      'invalidBankPhonePrefix': 'Phone number must start with 6, 7, 8, or 9',
      'invalidBankPhoneRepeated': 'Phone number cannot have all same digits',
      'invalidFormat': 'Please enter a valid 10-digit phone number'
    },
    // File upload error messages
    'aadharFile': {
      'required': 'Aadhar document is required',
      'invalidFormat': 'Please upload a valid Aadhar document'
    },
    'panFile': {
      'required': 'PAN document is required',
      'invalidFormat': 'Please upload a valid PAN document'
    },
    'gstFile': {
      'invalidFormat': 'Please upload a valid GST document'
    },
    'chequeFile': {
      'required': 'Cancelled cheque is required',
      'invalidFormat': 'Please upload a valid cancelled cheque'
    }
  };

  // Trip-specific error messages
  static readonly TRIP_ERRORS: FieldErrorMessages = {
    'sourceLocation': {
      'required': 'Source location is required',
      'invalidFormat': 'Please select a valid source location'
    },
    'destinationLocation': {
      'required': 'Destination location is required',
      'sameLocation': 'Source and destination cannot be the same',
      'invalidFormat': 'Please select a valid destination location'
    },
    'mode': {
      'required': 'Transport mode is required',
      'invalidFormat': 'Please select a valid transport mode'
    },
    'route': {
      'required': 'Route is required',
      'invalidFormat': 'Please select a valid route'
    },
    'vehicleType': {
      'required': 'Vehicle type is required',
      'invalidFormat': 'Please select a valid vehicle type'
    },
    'vendor': {
      'required': 'Vendor is required for vendor-based vehicles',
      'invalidFormat': 'Please select a valid vendor'
    },
    'vehicleNumber': {
      'required': 'Vehicle number is required',
      'minlength': 'Vehicle number must be at least 2 characters long',
      'pattern': 'Please enter a valid Indian vehicle registration number (e.g., KA01AB1234)',
      'invalidFormat': 'Please enter a valid vehicle number'
    },
    'fleetType': {
      'required': 'Fleet type is required for market-based vehicles',
      'invalidFormat': 'Please select a valid fleet type'
    },
    'loadCapacity': {
      'required': 'Load capacity is required for market-based vehicles',
      'pattern': 'Please enter a valid number (e.g., 1000 or 1000.5)',
      'min': 'Load capacity must be at least 100kg',
      'max': 'Load capacity cannot exceed 100,000kg',
      'invalidLoadCapacityFormat': 'Please enter a valid number (e.g., 1000 or 1000.5)',
      'invalidLoadCapacity': 'Please enter a valid load capacity',
      'invalidLoadCapacityRange': 'Load capacity must be between 100kg and 100,000kg',
      'invalidFormat': 'Please enter a valid load capacity'
    },
    'cost': {
      'required': 'Cost is required for market-based vehicles',
      'pattern': 'Please enter a valid number (e.g., 5000 or 5000.50)',
      'min': 'Cost must be at least 0',
      'max': 'Cost cannot exceed 1,000,000',
      'invalidCostFormat': 'Please enter a valid number (e.g., 5000 or 5000.50)',
      'invalidCost': 'Please enter a valid cost amount',
      'invalidCostRange': 'Cost must be between 0 and 1,000,000',
      'invalidFormat': 'Please enter a valid cost'
    },
    'driverType': {
      'required': 'Driver type is required',
      'invalidFormat': 'Please select a valid driver type'
    },
    'driverVendor': {
      'required': 'Driver vendor is required for vendor-based drivers',
      'invalidFormat': 'Please select a valid driver vendor'
    },
    'driverName': {
      'required': 'Driver name is required',
      'minlength': 'Driver name must be at least 2 characters long',
      'invalidFormat': 'Please enter a valid driver name'
    },
    'driverContact': {
      'required': 'Driver contact is required for market-based drivers',
      'pattern': 'Please enter a valid 10-digit Indian mobile number (starting with 6-9)',
      'invalidFormat': 'Please enter a valid driver contact number'
    },
    'origin': {
      'required': 'Origin is required',
      'minlength': 'Origin must be at least 2 characters long',
      'invalidFormat': 'Please enter a valid origin'
    },
    'destination': {
      'required': 'Destination is required',
      'minlength': 'Destination must be at least 2 characters long',
      'invalidFormat': 'Please enter a valid destination'
    },
    'startDate': {
      'required': 'Start date is required',
      'pastDate': 'Start date cannot be in the past',
      'invalidFormat': 'Please select a valid start date'
    },
    'endDate': {
      'required': 'End date is required',
      'invalidDateRange': 'End date must be after start date',
      'invalidFormat': 'Please select a valid end date'
    },
    'distance': {
      'required': 'Distance is required',
      'invalidPositiveNumber': 'Please enter a valid positive distance',
      'invalidFormat': 'Please enter a valid distance'
    },
    'estimatedDuration': {
      'required': 'Estimated duration is required',
      'invalidPositiveNumber': 'Please enter a valid positive duration',
      'invalidFormat': 'Please enter a valid duration'
    },
    'scheduleHour': {
      'required': 'Schedule hour is required',
      'min': 'Hour must be between 0 and 23',
      'max': 'Hour must be between 0 and 23',
      'invalidFormat': 'Please select a valid hour'
    },
    'scheduleMinute': {
      'required': 'Schedule minute is required',
      'min': 'Minute must be between 0 and 59',
      'max': 'Minute must be between 0 and 59',
      'invalidFormat': 'Please select a valid minute'
    },
    'recurrence': {
      'required': 'Recurrence pattern is required for recurring trips',
      'invalidFormat': 'Please select valid recurrence days'
    },
    'autoInitiate': {
      'invalidFormat': 'Please select a valid auto-initiate option'
    }
  };

  // Route-specific error messages
  static readonly ROUTE_ERRORS: FieldErrorMessages = {
    'routeName': {
      'required': 'Route name is required',
      'minlength': 'Route name must be at least 2 characters long',
      'invalidFormat': 'Please enter a valid route name'
    },
    'source': {
      'required': 'Source location is required',
      'invalidSource': 'Please select a valid source location',
      'sameAsDestination': 'Source and destination cannot be the same unless it\'s a round trip',
      'invalidFormat': 'Please select a valid source location'
    },
    'destination': {
      'required': 'Destination location is required',
      'invalidDestination': 'Please select a valid destination location',
      'sameAsSource': 'Destination cannot be the same as source unless it\'s a round trip',
      'invalidFormat': 'Please select a valid destination location'
    },
    'routeType': {
      'required': 'Route type is required',
      'invalidRouteType': 'Please select a valid route type (Network, Linehaul, or Local)',
      'invalidFormat': 'Please select a valid route type'
    },
    'modeOfTransport': {
      'required': 'Mode of transport is required',
      'invalidMode': 'Please select a valid mode of transport (Surface or Air)',
      'invalidFormat': 'Please select a valid mode of transport'
    },
    'routeStatus': {
      'required': 'Route status is required',
      'invalidStatus': 'Please select a valid route status (Active or Inactive)',
      'invalidFormat': 'Please select a valid route status'
    },
    'startTime': {
      'required': 'Start time is required',
      'invalidTimeFormat': 'Please enter time in format: "Day 0 | 15:30" or "15:30"',
      'invalidTimeRange': 'Time must be in 24-hour format (00:00 to 23:59)',
      'invalidFormat': 'Please enter a valid start time'
    },
    'totalDistance': {
      'required': 'Total distance is required',
      'invalidPositiveNumber': 'Please enter a valid positive distance',
      'invalidDistanceRange': 'Distance must be between 1 and 50,000 km',
      'invalidFormat': 'Please enter a valid distance'
    },
    'estimatedTime': {
      'required': 'Estimated time is required',
      'invalidPositiveNumber': 'Please enter a valid positive time',
      'invalidFormat': 'Please enter a valid time'
    },
    'isRoundTrip': {
      'invalidFormat': 'Please select a valid round trip option'
    },
    'selectCity': {
      'invalidFormat': 'Please select a valid city'
    },
    'destinationDistance': {
      'required': 'Destination distance is required',
      'invalidDistanceFormat': 'Please enter a valid distance format (e.g., "150 km")',
      'invalidDistanceRange': 'Distance must be between 1 and 50,000 km',
      'invalidFormat': 'Please enter a valid destination distance'
    },
    'destinationTravelTime': {
      'required': 'Destination travel time is required',
      'invalidTravelTimeFormat': 'Please enter travel time in format: "2 hr 30 min", "1 hr", or "45 min"',
      'invalidTravelTimeRange': 'Travel time must be between 1 minute and 72 hours',
      'invalidFormat': 'Please enter a valid travel time'
    },
    'destinationArrivalTime': {
      'required': 'Destination arrival time is required',
      'invalidArrivalTimeFormat': 'Please enter arrival time in format: "Day 0 | 15:30"',
      'invalidArrivalTime': 'Arrival time must be after departure time',
      'invalidFormat': 'Please enter a valid arrival time'
    },
    'touchPoints': {
      'required': 'At least one touch point is required for complex routes',
      'invalidTouchPoint': 'Touch point data is invalid',
      'duplicateTouchPoint': 'Duplicate touch points are not allowed',
      'invalidFormat': 'Please enter valid touch point information'
    }
  };

  // Touch Point specific error messages
  static readonly TOUCH_POINT_ERRORS: FieldErrorMessages = {
    'selectCity': {
      'required': 'City selection is required for touch point',
      'invalidFormat': 'Please select a valid city'
    },
    'cityHub': {
      'required': 'Hub selection is required for touch point',
      'invalidHub': 'Please select a valid hub',
      'duplicateHub': 'This hub is already used in another touch point',
      'invalidFormat': 'Please select a valid hub'
    },
    'cityHubId': {
      'required': 'Hub ID is required',
      'invalidFormat': 'Invalid hub ID'
    },
    'distance': {
      'required': 'Distance is required for touch point',
      'invalidDistanceFormat': 'Please enter a valid distance format (e.g., "150 km")',
      'invalidDistanceRange': 'Distance must be between 1 and 50,000 km',
      'invalidPositiveNumber': 'Distance must be a positive number',
      'invalidFormat': 'Please enter a valid distance'
    },
    'travelTime': {
      'required': 'Travel time is required for touch point',
      'invalidTravelTimeFormat': 'Please enter travel time in format: "2 hr 30 min", "1 hr", or "45 min"',
      'invalidTravelTimeRange': 'Travel time must be between 1 minute and 72 hours',
      'invalidFormat': 'Please enter a valid travel time'
    },
    'arrivalTime': {
      'required': 'Arrival time is required for touch point',
      'invalidArrivalTimeFormat': 'Please enter arrival time in format: "Day 0 | 15:30"',
      'invalidArrivalTime': 'Arrival time must be after previous departure time',
      'invalidFormat': 'Please enter a valid arrival time'
    },
    'departureTime': {
      'required': 'Departure time is required for touch point',
      'invalidDepartureTimeFormat': 'Please enter departure time in format: "Day 0 | 15:30"',
      'invalidDepartureTime': 'Departure time must be after arrival time',
      'invalidFormat': 'Please enter a valid departure time'
    }
  };

  // Transit-specific error messages
  static readonly TRANSIT_ERRORS: FieldErrorMessages = {
    'transitId': {
      'required': 'Transit ID is required',
      'invalidTransitIdLength': 'Transit ID must be at least 3 characters long',
      'invalidTransitIdChars': 'Transit ID can only contain letters, numbers, hyphens, and underscores',
      'invalidTransitIdConsecutive': 'Transit ID cannot have consecutive special characters',
      'invalidFormat': 'Please enter a valid transit ID'
    },
    'transitName': {
      'required': 'Transit name is required',
      'invalidTransitNameLength': 'Transit name must be at least 2 characters long',
      'invalidTransitNameChars': 'Transit name can only contain letters, numbers, spaces, dots, hyphens, and commas',
      'invalidTransitNameSpaces': 'Transit name cannot have consecutive spaces',
      'invalidTransitNameFormat': 'Transit name must start and end with a letter or number',
      'minlength': 'Transit name must be at least 2 characters long',
      'invalidFormat': 'Please enter a valid transit name'
    },
    'latitude': {
      'required': 'Latitude is required',
      'invalidLatitudeFormat': 'Please enter a valid latitude coordinate (e.g., 12.9716 or -12.9716)',
      'invalidLatitudeRange': 'Latitude must be between -90 and 90 degrees',
      'invalidFormat': 'Please enter a valid latitude coordinate',
      'invalidRange': 'Latitude must be between -90 and 90 degrees'
    },
    'longitude': {
      'required': 'Longitude is required',
      'invalidLongitudeFormat': 'Please enter a valid longitude coordinate (e.g., 77.5946 or -77.5946)',
      'invalidLongitudeRange': 'Longitude must be between -180 and 180 degrees',
      'invalidFormat': 'Please enter a valid longitude coordinate',
      'invalidRange': 'Longitude must be between -180 and 180 degrees'
    },
    'phoneNumber': {
      'required': 'Phone number is required',
      'pattern': 'Please enter a valid 10-digit phone number',
      'invalidFormat': 'Please enter a valid phone number'
    },
    'email': {
      'required': 'Email is required',
      'email': 'Please enter a valid email address',
      'invalidFormat': 'Please enter a valid email format(example@gmail.com)'
    },
    'transitManager': {
      'required': 'Transit manager is required',
      'invalidManagerNameLength': 'Transit manager name must be at least 2 characters long',
      'invalidManagerNameChars': 'Transit manager name can only contain letters, spaces, dots, and hyphens',
      'invalidManagerNameSpaces': 'Transit manager name cannot have consecutive spaces',
      'invalidManagerNameFormat': 'Transit manager name must start and end with a letter',
      'minlength': 'Transit manager name must be at least 2 characters long',
      'invalidFormat': 'Please enter a valid transit manager name'
    },
    'addressLine1': {
      'required': 'Address line 1 is required',
      'invalidAddressLength': 'Address must be at least 5 characters long',
      'invalidAddressChars': 'Address can only contain letters, numbers, spaces, dots, hyphens, commas, and slashes',
      'invalidAddressSpaces': 'Address cannot have consecutive spaces',
      'minlength': 'Address must be at least 5 characters long',
      'invalidFormat': 'Please enter a valid address'
    },
    'addressLine2': {
      'invalidFormat': 'Please enter a valid address line 2'
    },
    'pinCode': {
      'invalidFormat': 'Please enter a valid pincode'
    },
    'city': {
      'required': 'City is required',
      'invalidCityLength': 'City must be at least 2 characters long',
      'invalidCityChars': 'City can only contain letters, spaces, dots, and hyphens',
      'invalidCitySpaces': 'City cannot have consecutive spaces',
      'invalidCityFormat': 'City must start and end with a letter',
      'minlength': 'City must be at least 2 characters long',
      'invalidFormat': 'Please enter a valid city name'
    },
    'state': {
      'required': 'State is required',
      'invalidStateLength': 'State must be at least 2 characters long',
      'invalidStateChars': 'State can only contain letters, spaces, dots, and hyphens',
      'invalidStateSpaces': 'State cannot have consecutive spaces',
      'invalidStateFormat': 'State must start and end with a letter',
      'minlength': 'State must be at least 2 characters long',
      'invalidFormat': 'Please enter a valid state name'
    },
    'zone': {
      'required': 'Zone is required',
      'invalidZoneLength': 'Zone must be at least 1 character long',
      'invalidZoneChars': 'Zone can only contain letters, numbers, spaces, dots, and hyphens',
      'invalidFormat': 'Please select a valid zone'
    }
  };

  // File upload error messages
  static readonly FILE_ERRORS: ErrorMessages = {
    'invalidFileType': 'Invalid file type. Please upload {allowedTypes}',
    'fileTooLarge': 'File size must be less than {maxSize}MB',
    'invalidFileSize': 'File size is invalid',
    'fileRequired': 'File is required',
    'uploadFailed': 'File upload failed. Please try again',
    'invalidFileFormat': 'Invalid file format',
    'fileCorrupted': 'File appears to be corrupted',
    'unsupportedFile': 'Unsupported file type'
  };

  // Authentication error messages
  static readonly AUTH_ERRORS: ErrorMessages = {
    'invalidCredentials': 'Invalid username or password',
    'accountLocked': 'Account is locked. Please contact administrator',
    'sessionExpired': 'Session has expired. Please login again',
    'unauthorized': 'You are not authorized to access this resource',
    'forbidden': 'Access forbidden',
    'invalidToken': 'Invalid or expired token',
    'passwordMismatch': 'Passwords do not match',
    'weakPassword': 'Password is too weak. Please choose a stronger password',
    'invalidEmail': 'Please enter a valid email address(example@gmail.com)',
    'userNotFound': 'User not found',
    'emailAlreadyExists': 'Email address is already registered'
  };

  // API error messages
  static readonly API_ERRORS: ErrorMessages = {
    'networkError': 'Network error. Please check your connection',
    'serverError': 'Server error. Please try again later',
    'timeoutError': 'Request timeout. Please try again',
    'badRequest': 'Invalid request. Please check your input',
    'notFound': 'Resource not found',
    'conflict': 'Resource conflict. Please check your data',
    'validationError': 'Validation error. Please check your input',
    'internalServerError': 'Internal server error. Please try again later'
  };

  /**
   * Get error message for a specific field and error type
   * @param fieldName - The name of the field
   * @param errorType - The type of error
   * @param errorData - Additional error data for interpolation
   * @returns The formatted error message
   */
  static getErrorMessage(fieldName: string, errorType: string, errorData?: any): string {
    // Check vehicle errors first
    if (this.VEHICLE_ERRORS[fieldName] && this.VEHICLE_ERRORS[fieldName][errorType]) {
      return this.interpolateMessage(this.VEHICLE_ERRORS[fieldName][errorType], errorData);
    }

    // Check dimension errors
    if (this.DIMENSION_ERRORS[fieldName] && this.DIMENSION_ERRORS[fieldName][errorType]) {
      return this.interpolateMessage(this.DIMENSION_ERRORS[fieldName][errorType], errorData);
    }

    // Check driver errors
    if (this.DRIVER_ERRORS[fieldName] && this.DRIVER_ERRORS[fieldName][errorType]) {
      return this.interpolateMessage(this.DRIVER_ERRORS[fieldName][errorType], errorData);
    }

    // Check vendor errors
    if (this.VENDOR_ERRORS[fieldName] && this.VENDOR_ERRORS[fieldName][errorType]) {
      return this.interpolateMessage(this.VENDOR_ERRORS[fieldName][errorType], errorData);
    }

    // Check trip errors
    if (this.TRIP_ERRORS[fieldName] && this.TRIP_ERRORS[fieldName][errorType]) {
      return this.interpolateMessage(this.TRIP_ERRORS[fieldName][errorType], errorData);
    }

    // Check route errors
    if (this.ROUTE_ERRORS[fieldName] && this.ROUTE_ERRORS[fieldName][errorType]) {
      return this.interpolateMessage(this.ROUTE_ERRORS[fieldName][errorType], errorData);
    }

    // Check touch point errors
    if (this.TOUCH_POINT_ERRORS[fieldName] && this.TOUCH_POINT_ERRORS[fieldName][errorType]) {
      return this.interpolateMessage(this.TOUCH_POINT_ERRORS[fieldName][errorType], errorData);
    }

    // Check transit errors
    if (this.TRANSIT_ERRORS[fieldName] && this.TRANSIT_ERRORS[fieldName][errorType]) {
      return this.interpolateMessage(this.TRANSIT_ERRORS[fieldName][errorType], errorData);
    }

    // Check dimension ratio errors
    if (this.DIMENSION_RATIO_ERRORS[errorType]) {
      return this.interpolateMessage(this.DIMENSION_RATIO_ERRORS[errorType], errorData);
    }

    // Check file errors
    if (this.FILE_ERRORS[errorType]) {
      return this.interpolateMessage(this.FILE_ERRORS[errorType], errorData);
    }

    // Check auth errors
    if (this.AUTH_ERRORS[errorType]) {
      return this.interpolateMessage(this.AUTH_ERRORS[errorType], errorData);
    }

    // Check API errors
    if (this.API_ERRORS[errorType]) {
      return this.interpolateMessage(this.API_ERRORS[errorType], errorData);
    }

    // Check common errors
    if (this.COMMON_ERRORS[errorType]) {
      return this.interpolateMessage(this.COMMON_ERRORS[errorType], errorData);
    }

    // Default error message
    return 'Invalid input';
  }

  /**
   * Get all error messages for a specific field
   * @param fieldName - The name of the field
   * @returns Object containing all error messages for the field
   */
  static getFieldErrorMessages(fieldName: string): ErrorMessages {
    const vehicleErrors = this.VEHICLE_ERRORS[fieldName] || {};
    const dimensionErrors = this.DIMENSION_ERRORS[fieldName] || {};
    const driverErrors = this.DRIVER_ERRORS[fieldName] || {};
    const vendorErrors = this.VENDOR_ERRORS[fieldName] || {};
    const tripErrors = this.TRIP_ERRORS[fieldName] || {};
    const routeErrors = this.ROUTE_ERRORS[fieldName] || {};
    const touchPointErrors = this.TOUCH_POINT_ERRORS[fieldName] || {};
    const transitErrors = this.TRANSIT_ERRORS[fieldName] || {};

    return {
      ...vehicleErrors,
      ...dimensionErrors,
      ...driverErrors,
      ...vendorErrors,
      ...tripErrors,
      ...routeErrors,
      ...touchPointErrors,
      ...transitErrors
    };
  }

  /**
   * Interpolate placeholders in error messages
   * @param message - The error message with placeholders
   * @param data - Data to interpolate
   * @returns The interpolated message
   */
  private static interpolateMessage(message: string, data?: any): string {
    if (!data) return message;

    let interpolatedMessage = message;

    // Replace common placeholders
    if (data.minLength) {
      interpolatedMessage = interpolatedMessage.replace('{minLength}', data.minLength);
    }
    if (data.maxLength) {
      interpolatedMessage = interpolatedMessage.replace('{maxLength}', data.maxLength);
    }
    if (data.min) {
      interpolatedMessage = interpolatedMessage.replace('{min}', data.min);
    }
    if (data.max) {
      interpolatedMessage = interpolatedMessage.replace('{max}', data.max);
    }
    if (data.allowedTypes) {
      interpolatedMessage = interpolatedMessage.replace('{allowedTypes}', data.allowedTypes);
    }
    if (data.maxSize) {
      interpolatedMessage = interpolatedMessage.replace('{maxSize}', data.maxSize);
    }

    return interpolatedMessage;
  }

  /**
   * Get field display name
   * @param fieldName - The field name
   * @returns The display name for the field
   */
  static getFieldDisplayName(fieldName: string): string {
    const displayNames: { [key: string]: string } = {
      // Vehicle fields
      'vehicle': 'Vehicle',
      'vendor': 'Vendor',
      'vehicleMake': 'Vehicle Make',
      'vehicleModel': 'Vehicle Model',
      'loadCapacity': 'Load Capacity',
      'volLoadCapacity': 'Volumetric Load Capacity',
      'fuelType': 'Fuel Type',
      'yearOfManufacture': 'Year of Manufacture',
      'numberOfAxles': 'Number of Axles',
      'vehicleColor': 'Vehicle Color',
      'speed': 'Speed',
      'mileage': 'Mileage',
      'registrationNumber': 'Registration Number',
      'chasisNumber': 'Chassis Number',
      'lastServiceDate': 'Last Service Date',
      'nextServiceDue': 'Next Service Due',
      'serviceInterval': 'Service Interval',
      'currentOdometerReading': 'Current Odometer Reading',
      'wheelbase': 'Wheelbase',
      'vehicleFeet': 'Vehicle Feet',
      'gpsVendorName': 'GPS Provider',

      // Dimension fields
      'length': 'Length',
      'breadth': 'Breadth',
      'height': 'Height',

      // Driver fields
      'driverName': 'Driver Name',
      'firstName': 'First Name',
      'lastName': 'Last Name',
      'dob': 'Date of Birth',
      'driverType': 'Driver Type',
      'lastOrganizationName': 'Last Organization Name',
      'emergencyName': 'Emergency Contact Name',
      'emergencyPhone': 'Emergency Phone Number',
      'emergencyRelation': 'Emergency Relation',
      'licenseNumber': 'License Number',
      'licenseValidity': 'License Validity',
      'language': 'Language',
      'experience': 'Driving Experience',
      'dlClass': 'DL Class',
      'accidentHistory': 'Accident History',
      'address': 'Address',

      // Vendor fields
      'vendorName': 'Vendor Name',
      'organizationName': 'Organization Name',
      'ownerName': 'Owner Name',
      'email': 'Email',
      'phone': 'Phone Number',
      'vendorType': 'Vendor Type',
      'billingCycle': 'Billing Cycle',
      'addressLine1': 'Address Line 1',
      'addressLine2': 'Address Line 2',
      'pincode': 'Pincode',
      'city': 'City',
      'state': 'State',
      'country': 'Country',
      'aadhar': 'Aadhar Number',
      'pan': 'PAN Number',
      'gstNumber': 'GST Number',
      'gstYesNo': 'GST Number Required',
      'accountHolder': 'Account Holder Name',
      'bankName': 'Bank Name',
      'accountNumber': 'Account Number',
      'confirmAccountNumber': 'Confirm Account Number',
      'ifsc': 'IFSC Code',
      'branch': 'Branch Name',
      'bankPhone': 'Bank Phone Number',
      'aadharFile': 'Aadhar Document',
      'panFile': 'PAN Document',
      'gstFile': 'GST Document',
      'chequeFile': 'Cancelled Cheque',

      // Trip fields
      'origin': 'Origin',
      'startDate': 'Start Date',
      'endDate': 'End Date',
      'distance': 'Distance',
      'estimatedDuration': 'Estimated Duration',
      'sourceLocation': 'Source Location',
      'destinationLocation': 'Destination Location',
      'mode': 'Transport Mode',
      'route': 'Route',
      'vehicleType': 'Vehicle Type',
      'vehicleNumber': 'Vehicle Number',
      'fleetType': 'Fleet Type',
      'cost': 'Cost',
      'driverVendor': 'Driver Vendor',
      'driverContact': 'Driver Contact',
      'scheduleHour': 'Schedule Hour',
      'scheduleMinute': 'Schedule Minute',
      'recurrence': 'Recurrence Pattern',
      'autoInitiate': 'Auto Initiate',

      // Route fields
      'routeName': 'Route Name',
      'source': 'Source Location',
      'destination': 'Destination Location',
      'routeType': 'Route Type',
      'modeOfTransport': 'Mode of Transport',
      'routeStatus': 'Route Status',
      'startTime': 'Start Time',
      'totalDistance': 'Total Distance',
      'estimatedTime': 'Estimated Time',
      'isRoundTrip': 'Round Trip',
      'selectCity': 'Select City',
      'cityHub': 'City Hub',
      'destinationDistance': 'Destination Distance',
      'destinationTravelTime': 'Destination Travel Time',
      'destinationArrivalTime': 'Destination Arrival Time',
      'touchPoints': 'Touch Points',

      // Transit fields
      'transitId': 'Transit ID',
      'transitName': 'Transit Name',
      'latitude': 'Latitude',
      'longitude': 'Longitude',
      'transitManager': 'Transit Manager',
      'pinCode': 'Pincode',
      'zone': 'Zone'
    };

    return displayNames[fieldName] || fieldName;
  }

  /**
   * Get all validation errors for a form
   * @param form - The form group
   * @returns Array of error messages
   */
  static getFormValidationErrors(form: any): string[] {
    const errors: string[] = [];

    if (form.invalid) {
      Object.keys(form.controls).forEach(key => {
        const control = form.get(key);
        if (control?.invalid && control?.errors) {
          const fieldName = this.getFieldDisplayName(key);
          const errorMessage = this.getFieldErrorMessage(control, fieldName);
          if (errorMessage) {
            errors.push(`${fieldName}: ${errorMessage}`);
          }
        }
      });
    }

    return errors;
  }

  /**
   * Get error message for a specific form control
   * @param control - The form control
   * @param fieldName - The display name of the field
   * @returns The error message
   */
  static getFieldErrorMessage(control: any, fieldName: string): string {
    if (!control || !control.errors) return '';

    const errors = control.errors;

    // Check for specific error types
    for (const errorKey in errors) {
      const errorMessage = this.getErrorMessage(fieldName, errorKey, errors[errorKey]);
      if (errorMessage && errorMessage !== 'Invalid input') {
        return errorMessage;
      }
    }

    // Generic error messages
    if (errors['required']) return 'is required';
    if (errors['minlength']) return `must be at least ${errors['minlength'].requiredLength} characters`;
    if (errors['maxlength']) return `cannot exceed ${errors['maxlength'].requiredLength} characters`;
    if (errors['min']) return `must be at least ${errors['min'].min}`;
    if (errors['max']) return `cannot exceed ${errors['max'].max}`;
    if (errors['pattern']) return 'has invalid format';
    if (errors['email']) return 'must be a valid email address';

    return 'has an error';
  }
}
